import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  ChevronRight,
  Download,
  ExternalLink,
  FolderOpen,
  Mail,
  Package,
  Phone,
  Shield,
  ShieldCheck,
  Tag,
  Wifi,
} from 'lucide-react';
import { LOGO_SRC } from '../data/logo';
import { apiFetch, readApiJson } from '../lib/api';
import { formatPortalFileDate, getPortalFileHref } from '../lib/portalFiles';
import { LIEN_ASSISTANCE } from '../lib/assistance';

const SUPPORT_TOOLS = [
  // TeamViewer QuickSupport retiré (TeamViewer décommissionné — 2026-06-11).
  {
    // Notre QuickSupport (2026-08-28). Sert setup-assistance.cmd : télécharge le client
    // MeterisConnect PORTABLE, l'extrait dans %TEMP% et le lance. Rien n'est installé.
    // AnyDesk reste tant que celui-ci n'a pas fait ses preuves — on ne retire un outil de
    // prise en main que quand son remplaçant est éprouvé, jamais sur la foi d'un test.
    name: 'Assistance METERIS',
    description: "Notre outil de prise en main. Aucune installation : le programme s'arrête "
      + 'dès que vous le fermez.',
    // Icône prise parmi celles DÉJÀ importées ici : lucide-react n'est pas installé dans
    // le worktree, donc un nom d'icône non vérifiable casserait le build en déploiement.
    icon: ShieldCheck,
    url: LIEN_ASSISTANCE,
    internal: true,
    badge: 'Solution METERIS',
    color: '#0099D6',
  },
  {
    name: 'AnyDesk',
    description: 'Alternative légère pour la prise en main à distance lorsque le technicien vous le demande.',
    icon: Wifi,
    url: 'https://anydesk.com/fr/downloads/windows',
    badge: null,
    color: '#2D9CDB',
  },
];

const STEPS = [
  { num: '01', title: 'Téléchargez', desc: "Choisissez l'outil demandé par votre technicien et téléchargez-le." },
  { num: '02', title: 'Lancez', desc: 'Exécutez le programme. Aucune installation complexe n’est requise.' },
  { num: '03', title: 'Partagez le code', desc: 'Communiquez le code affiché à votre technicien Meteris.' },
];

function TagList({ tags = [] }) {
  if (!tags.length) return null;

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
      {tags.map(tag => (
        <span key={tag} style={publicTagStyle}>
          <Tag size={11} />
          {tag}
        </span>
      ))}
    </div>
  );
}

function ResourceCard({ resource }) {
  const href = getPortalFileHref(resource);
  const isExternal = resource.storage_kind === 'external';

  return (
    <div
      style={{
        background: '#15171E',
        border: '1px solid rgba(0,153,214,0.15)',
        borderRadius: 14,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div
        style={{
          width: 46,
          height: 46,
          background: 'rgba(0,153,214,0.12)',
          borderRadius: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FolderOpen size={22} color="#0099D6" />
      </div>

      <div>
        <h3 style={{ fontWeight: 700, fontSize: 16, margin: '0 0 8px 0' }}>{resource.name}</h3>
        <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
          {resource.description || 'Ressource publique mise à disposition par Meteris.'}
        </p>
        <TagList tags={resource.tags} />
      </div>

      <div style={{ display: 'grid', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
        <div>Source: {resource.provider_label}</div>
        {resource.size_label && <div>Taille: {resource.size_label}</div>}
        <div>Mise à jour: {formatPortalFileDate(resource.updated_at) || 'N/A'}</div>
      </div>

      <a
        href={href}
        {...(isExternal
          ? { target: '_blank', rel: 'noopener noreferrer' }
          : { download: resource.name || true }
        )}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          background: '#0099D6',
          color: '#fff',
          textDecoration: 'none',
          padding: '11px 20px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 700,
          marginTop: 'auto',
          width: 'fit-content',
        }}
      >
        {isExternal ? <ExternalLink size={15} /> : <Download size={15} />}
        {isExternal ? 'Ouvrir' : 'Télécharger'}
      </a>
    </div>
  );
}

export default function PublicPage() {
  // === TOUS LES HOOKS DOIVENT ETRE APPELES INCONDITIONNELLEMENT EN HAUT ===
  // (regle React : nombre de hooks identique entre renders, sinon crash)
  const { user, loading: authLoading, providers, defaultPath } = useAuth();
  const [lienSsoBureau, setLienSsoBureau] = useState('');
  const [publicResources, setPublicResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(true);
  const [resourceError, setResourceError] = useState('');

  // Visiteur depuis le réseau du bureau METERIS (METERIS_TRUSTED_IPS) et pas connecté :
  // on MET EN AVANT la connexion Microsoft, on ne l'impose plus. Jusqu'au 30/10/2026 la
  // page y partait d'office au bout de 400 ms ; tous les réseaux du bureau sortant par la
  // même IP Orange, un PC client posé dans le VLAN technique ne pouvait plus télécharger
  // l'assistance ni les agents. (Redirection inerte jusqu'au 25/09/2026 : derrière
  // Cloudflare l'IP du visiteur était mal lue — correctif e8b285bc9, qui l'a réveillée.)
  useEffect(() => {
    if (authLoading) return;
    if (user) return; // deja connecte, Navigate prend le relai plus bas
    let cancelled = false;
    (async () => {
      try {
        const response = await apiFetch('/api/auth/network-context');
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        if (data && data.is_internal_ip && providers?.microsoftEnabled && providers?.microsoftLoginUrl) {
          setLienSsoBureau(providers.microsoftLoginUrl);
        }
      } catch { /* silent : network context = best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, providers]);

  useEffect(() => { document.title = 'METERIS — Assistance Informatique'; }, []);

  useEffect(() => {
    let active = true;

    async function loadResources() {
      try {
        const response = await apiFetch('/api/files/public');
        const payload = await readApiJson(response);
        if (!response.ok || !payload.ok) {
          throw new Error(payload.error || 'Chargement impossible');
        }
        if (active) {
          setPublicResources(payload.files || []);
          setResourceError('');
        }
      } catch (err) {
        if (active) {
          setResourceError(err.message || 'Chargement impossible');
        }
      } finally {
        if (active) setLoadingResources(false);
      }
    }

    loadResources();
    return () => {
      active = false;
    };
  }, []);

  // === EARLY RETURNS conditionnels APRES les hooks ===
  // bi.meteris.fr → redirect straight to /bi
  if (typeof window !== 'undefined' && window.location.hostname === 'bi.meteris.fr') {
    return <Navigate to="/bi" replace />;
  }
  // rc.meteris.fr → redirect straight to /reception
  if (typeof window !== 'undefined' && window.location.hostname === 'rc.meteris.fr') {
    return <Navigate to="/reception" replace />;
  }
  // Deja connecte -> dashboard de l'user
  if (user) {
    return <Navigate to={defaultPath || '/dashboard'} replace />;
  }

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={LOGO_SRC} alt="METERIS" style={{ height: 36 }} />
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: 3, color: '#fff' }}>METERIS</div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: '#0099D6' }}>ASSISTANCE INFORMATIQUE</div>
          </div>
        </div>
        <Link
          data-testid="header-login-link"
          to="/login"
          style={headerLoginLinkStyle}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,153,214,0.2)'; e.currentTarget.style.borderColor = 'rgba(0,153,214,0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,153,214,0.1)'; e.currentTarget.style.borderColor = 'rgba(0,153,214,0.3)'; }}
        >
          Se connecter <ChevronRight size={14} />
        </Link>
      </header>

      <section style={heroSectionStyle}>
        {lienSsoBureau && (
          <div>
          <a
            data-testid="internal-network-login"
            href={lienSsoBureau}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 18,
              padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              color: '#fff', textDecoration: 'none',
              background: 'rgba(0,153,214,0.18)', border: '1px solid rgba(0,153,214,0.5)',
            }}
          >
            Réseau METERIS détecté — se connecter avec Microsoft <ChevronRight size={14} />
          </a>
          </div>
        )}

        <div data-testid="public-page-badge" style={badgeStyle}>
          <span style={badgeDotStyle} />
          Support disponible
        </div>

        <h1 style={heroTitleStyle}>
          Assistance, agents
          <br />
          et <span style={{ color: '#0099D6' }}>téléchargements</span>
        </h1>

        <p style={heroTextStyle}>
          Retrouvez ici les outils de prise en main à distance et les ressources publiques mises à disposition par Meteris,
          sans authentification préalable.
        </p>

        <div style={stepsGridStyle}>
          {STEPS.map(step => (
            <div key={step.num} style={stepCardStyle}>
              <div style={stepNumStyle}>{step.num}</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{step.title}</div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, lineHeight: 1.6 }}>{step.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={sectionStyle}>
        <div style={sectionHeadingWrapStyle}>
          <div>
            <h2 style={sectionTitleStyle}>Assistance à distance</h2>
            <p style={sectionSubtitleStyle}>Choisissez l’outil recommandé par votre technicien.</p>
          </div>
        </div>

        <div style={resourceGridStyle}>
          {SUPPORT_TOOLS.map(tool => {
            const Icon = tool.icon;
            return (
              <div key={tool.name} style={toolCardStyle}>
                {tool.badge && <div style={toolBadgeStyle}>{tool.badge}</div>}
                <div style={toolIconWrapStyle}>
                  <Icon size={24} color={tool.color} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 style={{ fontWeight: 700, fontSize: 16, margin: '0 0 8px 0' }}>{tool.name}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                    {tool.description}
                  </p>
                </div>
                <a
                  data-testid={`download-${tool.name.toLowerCase().replace(/ /g, '-')}`}
                  href={tool.url}
                  // Un fichier servi par le portail se télécharge sur place : ouvrir un
                  // onglet pour le refermer aussitôt inquiète un client déjà en panne.
                  {...(tool.internal
                    ? { download: '' }
                    : { target: '_blank', rel: 'noopener noreferrer' })}
                  style={toolButtonStyle}
                >
                  <Download size={15} />
                  Télécharger
                </a>
              </div>
            );
          })}
        </div>
      </section>

      <section style={sectionStyle}>
        <div style={sectionHeadingWrapStyle}>
          <div>
            <h2 style={sectionTitleStyle}>Ressources publiques Meteris</h2>
            <p style={sectionSubtitleStyle}>
              Agents, installateurs ou documents exposés depuis le back-office du portail.
            </p>
          </div>
          <div style={publicInfoBadgeStyle}>
            <ShieldCheck size={14} />
            accès public
          </div>
        </div>

        {loadingResources ? (
          <div style={emptyStateStyle}>Chargement des ressources publiques...</div>
        ) : resourceError ? (
          <div style={{ ...emptyStateStyle, color: '#ffb4b4' }}>{resourceError}</div>
        ) : publicResources.length === 0 ? (
          <div style={emptyStateStyle}>
            Aucune ressource publique supplémentaire n’est encore publiée.
          </div>
        ) : (
          <div style={resourceGridStyle}>
            {publicResources.map(resource => <ResourceCard key={resource.id} resource={resource} />)}
          </div>
        )}
      </section>

      <section style={sectionStyle}>
        <div style={sectionHeadingWrapStyle}>
          <div>
            <h2 style={sectionTitleStyle}>Package d’installation personnalisable</h2>
            <p style={sectionSubtitleStyle}>
              Une ressource distincte des fichiers classiques pour générer un script PowerShell d’installation silencieuse.
            </p>
          </div>
          <div style={publicInfoBadgeStyle}>
            <Package size={14} />
            package builder
          </div>
        </div>

        <div style={builderHighlightStyle}>
          <div style={{ display: 'grid', gap: 12, flex: 1, minWidth: 260 }}>
            <div style={builderBadgeStyle}>
              <ShieldCheck size={14} />
              Compatible PowerShell + WinGet
            </div>
            <h3 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
              Sélectionnez vos logiciels puis téléchargez le script prêt à exécuter
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.56)', fontSize: 14, lineHeight: 1.8, margin: 0 }}>
              Chrome, Firefox, 7-Zip, LibreOffice, Teams, OneDrive, Dropbox, VLC, Greenshot, Malwarebytes
              et d’autres applications vérifiées peuvent être regroupés dans un package unique.
            </p>
          </div>

          <div style={{ display: 'grid', gap: 12, minWidth: 240 }}>
            <Link to="/package-builder" style={builderButtonStyle}>
              <Package size={16} />
              Ouvrir le générateur
            </Link>
            <div style={builderInfoStyle}>
              Option administrateur incluse pour relancer le script avec les droits nécessaires.
            </div>
          </div>
        </div>
      </section>

      <section style={contactSectionStyle}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 6px 0' }}>Contacter Meteris</h2>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: 0 }}>
            Notre équipe reste disponible pendant les heures ouvrables.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <a href="tel:+33XXXXXXXXX" style={contactLinkStyle}>
            <Phone size={14} /> Téléphone
          </a>
          <a href="mailto:support@meteris.fr" style={contactLinkStyle}>
            <Mail size={14} /> Email
          </a>
        </div>
      </section>

      <footer style={footerStyle}>
        <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>
          Copyright 2026 Meteris. Tous droits réservés.
        </span>
      </footer>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  background: 'radial-gradient(circle at top, rgba(0,153,214,0.16), transparent 35%), #0B0C10',
  color: '#fff',
  fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
};

const headerStyle = {
  padding: '20px 40px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 14,
  background: 'rgba(21,23,30,0.8)',
  backdropFilter: 'blur(12px)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

const headerLoginLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  color: '#0099D6',
  textDecoration: 'none',
  fontSize: 13,
  fontWeight: 700,
  padding: '8px 16px',
  borderRadius: 8,
  border: '1px solid rgba(0,153,214,0.3)',
  background: 'rgba(0,153,214,0.1)',
  transition: 'all 0.15s ease',
  letterSpacing: 0.5,
};

const heroSectionStyle = {
  padding: '80px 40px 56px',
  maxWidth: 980,
  margin: '0 auto',
  textAlign: 'center',
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'rgba(0,153,214,0.1)',
  border: '1px solid rgba(0,153,214,0.3)',
  borderRadius: 20,
  padding: '6px 16px',
  fontSize: 11,
  color: '#0099D6',
  letterSpacing: 1.5,
  marginBottom: 28,
  textTransform: 'uppercase',
};

const badgeDotStyle = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  background: '#0099D6',
  display: 'inline-block',
  animation: 'pulse 2s infinite',
};

const heroTitleStyle = {
  fontSize: 'clamp(34px, 5vw, 58px)',
  fontWeight: 800,
  lineHeight: 1.08,
  margin: '0 0 20px 0',
  letterSpacing: -1,
};

const heroTextStyle = {
  fontSize: 16,
  color: 'rgba(255,255,255,0.55)',
  lineHeight: 1.7,
  maxWidth: 620,
  margin: '0 auto 48px',
};

const stepsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const stepCardStyle = {
  background: '#15171E',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
  padding: '24px 20px',
  textAlign: 'left',
};

const stepNumStyle = {
  color: '#0099D6',
  fontWeight: 800,
  fontSize: 28,
  marginBottom: 10,
  letterSpacing: -1,
};

const sectionStyle = {
  padding: '0 40px 68px',
  maxWidth: 980,
  margin: '0 auto',
};

const sectionHeadingWrapStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  gap: 16,
  marginBottom: 24,
  flexWrap: 'wrap',
};

const sectionTitleStyle = {
  fontSize: 20,
  fontWeight: 700,
  marginBottom: 6,
  color: 'rgba(255,255,255,0.85)',
  letterSpacing: 0.5,
};

const sectionSubtitleStyle = {
  color: 'rgba(255,255,255,0.45)',
  fontSize: 13,
  margin: 0,
};

const resourceGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: 20,
};

const toolCardStyle = {
  background: '#15171E',
  border: '1px solid rgba(0,153,214,0.15)',
  borderRadius: 14,
  padding: 28,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  position: 'relative',
};

const toolBadgeStyle = {
  position: 'absolute',
  top: 16,
  right: 16,
  background: '#0099D6',
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
  padding: '3px 10px',
  borderRadius: 20,
  letterSpacing: 1,
  textTransform: 'uppercase',
};

const toolIconWrapStyle = {
  width: 48,
  height: 48,
  background: 'rgba(0,153,214,0.12)',
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const toolButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  background: '#0099D6',
  color: '#fff',
  textDecoration: 'none',
  padding: '11px 20px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 700,
  marginTop: 'auto',
  width: 'fit-content',
};

const publicInfoBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderRadius: 999,
  border: '1px solid rgba(0,153,214,0.3)',
  background: 'rgba(0,153,214,0.1)',
  color: '#7dd3fc',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1,
};

const emptyStateStyle = {
  border: '1px dashed rgba(255,255,255,0.12)',
  borderRadius: 14,
  padding: 20,
  background: 'rgba(255,255,255,0.03)',
  color: 'rgba(255,255,255,0.55)',
  fontSize: 13,
};

const publicTagStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 9px',
  borderRadius: 999,
  border: '1px solid rgba(0,153,214,0.22)',
  background: 'rgba(0,153,214,0.08)',
  color: '#9ddcff',
  fontSize: 11,
  fontWeight: 700,
};

const builderHighlightStyle = {
  display: 'flex',
  gap: 24,
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 28,
  borderRadius: 18,
  background: 'linear-gradient(135deg, rgba(0,153,214,0.14), rgba(255,255,255,0.03))',
  border: '1px solid rgba(0,153,214,0.18)',
};

const builderBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 12px',
  borderRadius: 999,
  border: '1px solid rgba(0,153,214,0.28)',
  background: 'rgba(0,153,214,0.1)',
  color: '#7dd3fc',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 1,
  width: 'fit-content',
};

const builderButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  background: '#0099D6',
  color: '#fff',
  textDecoration: 'none',
  padding: '12px 18px',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 700,
};

const builderInfoStyle = {
  color: 'rgba(255,255,255,0.5)',
  fontSize: 12,
  lineHeight: 1.7,
  maxWidth: 280,
};

const contactSectionStyle = {
  borderTop: '1px solid rgba(255,255,255,0.06)',
  padding: '44px 40px',
  maxWidth: 980,
  margin: '0 auto',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 24,
  alignItems: 'center',
};

const contactLinkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  color: 'rgba(255,255,255,0.7)',
  textDecoration: 'none',
  fontSize: 13,
  padding: '10px 18px',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.08)',
  fontFamily: 'inherit',
};

const footerStyle = {
  borderTop: '1px solid rgba(255,255,255,0.04)',
  padding: '24px 40px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
};

